import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../components/ui/button.jsx";
import { Card } from "../components/ui/card.jsx";
import { Input } from "../components/ui/input.jsx";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar.jsx";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group.jsx";
import AgentPanel from "../components/ai/AgentPanel.jsx";
import MultiPeerCall from "../components/realtime/MultiPeerCall.jsx";
import HighlightsPanel from "../components/realtime/HighlightsPanel.jsx";
import PollsPanel from "../components/realtime/PollsPanel.jsx";
import { Video, Mic, MicOff, VideoOff, MonitorUp, Users, MessageSquare, PhoneOff, Phone, Plus, MoreVertical, Sparkles, MonitorCog, MonitorPause, MonitorX, Smile, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import { connectSocket, releaseSocket } from "../lib/socket-client.js";
import SocketDiagnostics from "../components/realtime/SocketDiagnostics.jsx";
import EmojiPicker from "../components/ui/emoji-picker.jsx";
import "../global.css"

export default function Call() {
  const [incoming, setIncoming] = useState(!!new URLSearchParams(window.location.search).get("incoming"));
  const [joined, setJoined] = useState(!incoming);
  const [muted, setMuted] = useState(false);
  const [camera, setCamera] = useState(true);
  const [presenting, setPresenting] = useState(false);
  const [showParticipants, setShowParticipants] = useState(true);
  const [openParticipantsSheet, setOpenParticipantsSheet] = useState(false);
  const [openChatSheet, setOpenChatSheet] = useState(false);
  const [tab, setTab] = useState("participants");
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const preVideoRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const voiceRecorderRef = useRef(null);
  const voiceStreamRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const voiceTimerRef = useRef(null);
  const voiceCancelRef = useRef(false);
  const room = useMemo(() => new URLSearchParams(window.location.search).get("room") || "call:demo", []);
  const displayName = useMemo(() => new URLSearchParams(window.location.search).get("name") || "You", []);
  const localStreamRef = useRef(null);
  const [chat, setChat] = useState("");
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [messages, setMessages] = useState([]);
  const chatListRef = useRef(null);
  const inCallListRef = useRef(null);
  const callId = useMemo(() => (room.startsWith("call:") ? room.slice(5) : room), [room]);
  const [participants, setParticipants] = useState([
    { id: "u1", name: "Kathryn Murphy", role: "UI/UX Designer" },
    { id: "u2", name: "Kaylynn Kenter", role: "Product Designer" },
    { id: "u3", name: "Jocelyn Mango", role: "Software Developer" },
    { id: "u4", name: "Mira Arcand", role: "Data Analyst" },
  ]);
  const [banner, setBanner] = useState("");
  const [layout, setLayout] = useState("default");
  const [pip, setPip] = useState(false);
  const [openLayout, setOpenLayout] = useState(false);
  const [openReport, setOpenReport] = useState(false);
  const [openAbuse, setOpenAbuse] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [dialNumber, setDialNumber] = useState("");
  const [dialSending, setDialSending] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [issueType, setIssueType] = useState("");
  const [abuseType, setAbuseType] = useState("");
  const [abuseUserId, setAbuseUserId] = useState();
  const [abuseNotes, setAbuseNotes] = useState("");
  const [shot, setShot] = useState(null);
  const [micId, setMicId] = useState();
  const [camId, setCamId] = useState();
  const [spkId, setSpkId] = useState();
  const [devices, setDevices] = useState({ mics: [], cams: [], speakers: [] });
  const [recentCallHistory, setRecentCallHistory] = useState([]);
  const [preChecking, setPreChecking] = useState(false);
  const [preCamOk, setPreCamOk] = useState(false);
  const [preMicOk, setPreMicOk] = useState(false);
  const [preSpeakerOk, setPreSpeakerOk] = useState(false);
  const [preError, setPreError] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const micAnalyserRef = useRef(null);
  const micAnimationRef = useRef(0);
  const [pollSubtitle, setPollSubtitle] = useState("");
  const [meetingTimeLabel, setMeetingTimeLabel] = useState(() => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recogRef = useRef(null);
  const mediaRecRef = useRef(null);
  const mixDestRef = useRef(null);

  // System settings state
  const [audioNoiseSuppression, setAudioNoiseSuppression] = useState(true);
  const [audioEchoCancellation, setAudioEchoCancellation] = useState(true);
  const [audioAutoGain, setAudioAutoGain] = useState(true);
  const [videoResolution, setVideoResolution] = useState("720p");

  // Present options
  const [presentOptsOpen, setPresentOptsOpen] = useState(false);
  const [includeSystemAudio, setIncludeSystemAudio] = useState(true);
  const [localStatus, setLocalStatus] = useState({ audio: false, video: false });
  const [remoteStatus, setRemoteStatus] = useState({ audio: false, video: false });

  const send = (textOrNull) => {
    const msg = (textOrNull ?? chat).trim();
    if (!msg) return;
    const raw = msg;
    // Commands: /poll question|opt1,opt2  and /vote opt
    if (socketRef.current && room) {
      if (/^\/poll\s+/i.test(raw)) {
        const body = raw.replace(/^\/poll\s+/i, "");
        const [q, rest] = body.split("|");
        const opts = (rest || "").split(",").map(s=>s.trim()).filter(Boolean);
        if (q && opts.length>=2) socketRef.current.emit("new-poll", { roomId: room, question: q.trim(), options: opts });
        setChat("");
        return;
      }
      if (/^\/vote\s+/i.test(raw)) {
        const opt = raw.replace(/^\/vote\s+/i, "").trim();
        if (opt) socketRef.current.emit("vote", { roomId: room, option: opt });
        setChat("");
        return;
      }
    }

    const text = raw;
    setChat("");
    if (callId && socketRef.current) {
      socketRef.current.emit("call:message", { callId, authorEmail: "you@example.com", text });
    }
  };

  const startVoiceRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice messages are not supported on this device");
      return;
    }
    if (recordingVoice) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      voiceRecorderRef.current = recorder;
      voiceChunksRef.current = [];
      voiceCancelRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) voiceChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        if (voiceTimerRef.current) {
          clearTimeout(voiceTimerRef.current);
          voiceTimerRef.current = null;
        }
        const tracks = voiceStreamRef.current?.getTracks() || [];
        tracks.forEach((track) => track.stop());
        voiceStreamRef.current = null;
        const blob = new Blob(voiceChunksRef.current, { type: "audio/webm" });
        voiceChunksRef.current = [];
        const cancelled = voiceCancelRef.current;
        voiceCancelRef.current = false;
        setRecordingVoice(false);
        if (cancelled) return;
        if (blob.size > 0) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = String(reader.result || "");
            if (dataUrl) {
              send(`VOICE_DATA:${dataUrl}`);
              toast.success("Voice message sent");
            }
          };
          reader.readAsDataURL(blob);
        }
      };
      recorder.start(250);
      setRecordingVoice(true);
      voiceTimerRef.current = setTimeout(() => {
        if (voiceRecorderRef.current && voiceRecorderRef.current.state === "recording") {
          voiceRecorderRef.current.stop();
        }
      }, 60000);
    } catch (error) {
      const tracks = voiceStreamRef.current?.getTracks() || [];
      tracks.forEach((track) => track.stop());
      voiceStreamRef.current = null;
      voiceRecorderRef.current = null;
      voiceChunksRef.current = [];
      voiceCancelRef.current = false;
      setRecordingVoice(false);
      toast.error("Unable to access microphone for voice message");
    }
  };

  const stopVoiceRecording = (cancel = false) => {
    const recorder = voiceRecorderRef.current;
    if (!recorder) {
      voiceCancelRef.current = false;
      return;
    }
    voiceCancelRef.current = cancel;
    if (voiceTimerRef.current) {
      clearTimeout(voiceTimerRef.current);
      voiceTimerRef.current = null;
    }
    if (recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {}
    }
    voiceRecorderRef.current = null;
  };

  const handleVoiceButton = async () => {
    if (recordingVoice) {
      stopVoiceRecording(false);
    } else {
      await startVoiceRecording();
    }
  };

  const attachInputRef = useRef(null);
  const attachSheetRef = useRef(null);

  async function triggerPhoneCallDemo(){
    const number = dialNumber.trim();
    if (!number) {
      toast.error("Enter a phone number to dial");
      return;
    }
    if (!callId) {
      toast.error("Missing call id");
      return;
    }
    setDialSending(true);
    try {
      const res = await fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/invite-phone`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ phone: number, fromName: displayName }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Unable to trigger phone call");
      }
      toast.success(`Calling ${number}`);
      setDialNumber("");
    } catch (error) {
      toast.error(error.message || "Unable to trigger phone call");
    } finally {
      setDialSending(false);
    }
  }

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("presenting")) setPresenting(true);
  }, []);

  useEffect(() => () => {
    stopVoiceRecording(true);
    const tracks = voiceStreamRef.current?.getTracks() || [];
    tracks.forEach((track) => track.stop());
    voiceStreamRef.current = null;
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setMeetingTimeLabel(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // Poll stream statuses for overlays
  useEffect(() => {
    const tick = () => {
      try {
        const ls = localStreamRef.current;
        const rv = remoteVideoRef.current;
        const rs = rv && rv.srcObject ? rv.srcObject : null;
        const has = (s, kind) => !!(s && s.getTracks().some(t => t.kind===kind && t.readyState === "live" && t.enabled));
        setLocalStatus({ audio: has(ls, "audio"), video: has(ls, "video") });
        setRemoteStatus({ audio: has(rs, "audio"), video: has(rs, "video") });
      } catch {}
    };
    const id = setInterval(tick, 800);
    tick();
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!joined) return;

    const socket = connectSocket();
    socketRef.current = socket;
    const me = displayName;
    socket.emit("room:join", { room, profile: { name: me } });
    if (callId) socket.emit("call:join", { callId, name: me });

    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;

    const handleTrack = (ev) => {
      const [stream] = ev.streams;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
    };
    const handleIceCandidate = (ev) => {
      if (ev.candidate) socket.emit("webrtc:signal", { room, data: { candidate: ev.candidate } });
    };
    pc.ontrack = handleTrack;
    pc.onicecandidate = handleIceCandidate;

    (async () => {
      let stream = localStreamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
      }
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      socket.emit("webrtc:signal", { room, data: { ready: true } });
    })();

    const handlePresence = (ev) => {
      if (!ev || ev.userId === socket.id) return;
      const name = ev.name || "Someone";
      if (ev.type === "join") setBanner(`${name} has joined the video call`);
      if (ev.type === "leave") setBanner(`${name} has left the video call`);
      setTimeout(() => setBanner(""), 3000);
    };

    if (callId) {
      fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/messages`).then((r) => r.json()).then((list) => {
        setMessages(list.map((m) => ({ id: m.id, text: m.text, author: (m.authorEmail && m.authorEmail === "you@example.com") ? "You" : (m.authorName || m.authorEmail || "Member"), authorName: m.authorName || null, authorEmail: m.authorEmail || null, ts: m.ts })));
      }).catch(() => {});
    }

    const handleCallMessage = (m) => {
      setMessages((prev) => [...prev, { id: m.id, text: m.text, author: m.authorEmail === "you@example.com" ? "You" : (m.authorName || m.authorEmail || "Member"), authorName: m.authorName || null, authorEmail: m.authorEmail || null, ts: m.ts }]);
    };

    const handleCallEnd = () => {
      setBanner("Call ended");
      setTimeout(() => (window.location.search = "/app"), 1200);
    };

    const handleNewPoll = ({ question }) => {
      if (question) {
        setPollSubtitle(question);
        setTimeout(() => setPollSubtitle(""), 8000);
      }
    };

    const handleSignal = async (data) => {
      if (data?.ready) {
        if (!pc.currentRemoteDescription) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit("webrtc:signal", { room, data: { description: pc.localDescription } });
        }
        return;
      }
      if (data?.description) {
        const desc = data.description;
        if (desc.type === "offer") {
          await pc.setRemoteDescription(desc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit("webrtc:signal", { room, data: { description: pc.localDescription } });
        } else {
          await pc.setRemoteDescription(desc);
        }
        return;
      }
      if (data?.candidate) {
        try {
          await pc.addIceCandidate(data.candidate);
        } catch {}
      }
    };

    const handlePresent = ({ action, name }) => {
      if (action === "start") {
        setBanner(`${name || "Someone"} started presenting`);
      } else {
        setBanner(`${name || "Someone"} stopped presenting`);
      }
      setTimeout(() => setBanner(""), 2500);
    };

    socket.on("presence", handlePresence);
    socket.on("call:message", handleCallMessage);
    socket.on("call:end", handleCallEnd);
    socket.on("new-poll", handleNewPoll);
    socket.on("webrtc:signal", handleSignal);
    socket.on("call:present", handlePresent);

    return () => {
      socket.off("presence", handlePresence);
      socket.off("call:message", handleCallMessage);
      socket.off("call:end", handleCallEnd);
      socket.off("new-poll", handleNewPoll);
      socket.off("webrtc:signal", handleSignal);
      socket.off("call:present", handlePresent);
      pc.ontrack = null;
      pc.onicecandidate = null;
      try {
        pc.close();
      } catch {}
      releaseSocket();
    };
  }, [joined, room, callId, displayName]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((list) => {
      setDevices({
        mics: list.filter((d) => d.kind === "audioinput"),
        cams: list.filter((d) => d.kind === "videoinput"),
        speakers: list.filter((d) => d.kind === "audiooutput"),
      });
    });
  }, []);

  // Auto-scroll both chat lists to bottom on new messages
  useEffect(() => {
    const el1 = chatListRef.current;
    const el2 = inCallListRef.current;
    if (el1) requestAnimationFrame(() => { el1.scrollTop = el1.scrollHeight; });
    if (el2) requestAnimationFrame(() => { el2.scrollTop = el2.scrollHeight; });
  }, [messages.length, openChatSheet]);

  // Pre-join device test
  useEffect(() => {
    if (joined) return;
    let ac = null; let raf = 0;
    async function run(){
      setPreChecking(true); setPreError("");
      try{
        const constraints = { video: camId ? { deviceId: { exact: camId } } : true, audio: micId ? { deviceId: { exact: micId } } : true };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStreamRef.current = stream;
        setPreCamOk(stream.getVideoTracks().length > 0);
        setPreMicOk(stream.getAudioTracks().length > 0);
        if (preVideoRef.current) preVideoRef.current.srcObject = stream;
        // mic meter
        const AC = window.AudioContext || window.webkitAudioContext; ac = new AC();
        const src = ac.createMediaStreamSource(stream);
        const analyser = ac.createAnalyser(); analyser.fftSize = 512; micAnalyserRef.current = analyser; src.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        const tick = () => { analyser.getByteTimeDomainData(data); let sum=0; for(let i=0;i<data.length;i++){ const v=(data[i]-128)/128; sum += v*v; } const rms = Math.sqrt(sum/data.length); setMicLevel(rms); raf = requestAnimationFrame(tick); };
        raf = requestAnimationFrame(tick);
      }catch(err){ setPreError("Unable to access camera/microphone. Check permissions."); setPreCamOk(false); setPreMicOk(false); }
      finally{ setPreChecking(false); }
    }
    run();
    return () => { cancelAnimationFrame(raf); try{ ac?.close?.(); }catch{} };
  }, [joined, camId, micId]);

  useEffect(() => {
    const el = chatListRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages.length, openChatSheet]);

  useEffect(() => {
    if (!micId) return;
    (async () => {
      try {
        const ms = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: micId } } });
        const track = ms.getAudioTracks()[0];
        const pc = pcRef.current;
        const stream = localStreamRef.current;
        if (stream && track) {
          stream.getAudioTracks().forEach((t) => t.stop());
          stream.addTrack(track);
          const sender = pc?.getSenders().find((s) => s.track && s.track.kind === "audio");
          sender?.replaceTrack(track);
        }
      } catch {}
    })();
  }, [micId]);

  useEffect(() => {
    if (!camId) return;
    (async () => {
      try {
        const ms = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: camId } } });
        const track = ms.getVideoTracks()[0];
        const pc = pcRef.current;
        const stream = localStreamRef.current;
        if (stream && track) {
          stream.getVideoTracks().forEach((t) => t.stop());
          stream.addTrack(track);
          const sender = pc?.getSenders().find((s) => s.track && s.track.kind === "video");
          sender?.replaceTrack(track);
        }
      } catch {}
    })();
  }, [camId]);

  useEffect(() => {
    if (!spkId) return;
    const v = remoteVideoRef.current;
    if (v && typeof v.setSinkId === "function") {
      v.setSinkId(spkId).catch(() => {});
    }
  }, [spkId]);

  async function applyAudioSettings() {
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: audioNoiseSuppression,
          echoCancellation: audioEchoCancellation,
          autoGainControl: audioAutoGain,
        },
      });
      const newTrack = ms.getAudioTracks()[0];
      const pc = pcRef.current;
      const stream = localStreamRef.current;
      if (stream && newTrack) {
        stream.getAudioTracks().forEach((t) => t.stop());
        stream.addTrack(newTrack);
        const sender = pc?.getSenders().find((s) => s.track && s.track.kind === "audio");
        if (sender) await sender.replaceTrack(newTrack);
        newTrack.enabled = !muted;
      }
    } catch {}
  }

  async function applyVideoResolution() {
    if (presenting) return; // do not override during share
    const map = { "480p": { width: { ideal: 640 }, height: { ideal: 480 } }, "720p": { width: { ideal: 1280 }, height: { ideal: 720 } }, "1080p": { width: { ideal: 1920 }, height: { ideal: 1080 } } };
    const contraints = map[videoResolution] || map["720p"];
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ video: { ...contraints, deviceId: camId ? { exact: camId } : undefined } });
      const newTrack = ms.getVideoTracks()[0];
      const pc = pcRef.current;
      const stream = localStreamRef.current;
      if (stream && newTrack) {
        stream.getVideoTracks().forEach((t) => t.stop());
        stream.addTrack(newTrack);
        const sender = pc?.getSenders().find((s) => s.track && s.track.kind === "video");
        if (sender) await sender.replaceTrack(newTrack);
        newTrack.enabled = camera;
      }
    } catch {}
  }

  const takeScreenshot = () => {
    const v = remoteVideoRef.current || localVideoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    const vw = v.videoWidth || 640;
    const vh = v.videoHeight || 360;
    canvas.width = vw; canvas.height = vh;
    const ctx = canvas.getContext("2d");
    if (ctx) { ctx.drawImage(v, 0, 0, vw, vh); setShot(canvas.toDataURL("image/png")); }
  };

  const startTranscription = async () => {
    if (transcribing) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const recog = new SR();
      recog.lang = "en-US";
      recog.continuous = true; recog.interimResults = false;
      recog.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            const t = e.results[i][0].transcript;
            setTranscript((prev) => prev + (prev ? "\n" : "") + t);
            try { const rid = room; socketRef.current?.emit("meeting:text", { roomId: rid, text: t }); } catch {}
          }
        }
      };
      recog.onerror = () => {};
      recogRef.current = recog;
      recog.start();
      setTranscribing(true);
      return;
    }
    try {
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      const dest = ac.createMediaStreamDestination();
      mixDestRef.current = dest;
      const srcs = [];
      if (localStreamRef.current) srcs.push(ac.createMediaStreamSource(localStreamRef.current));
      const rv = remoteVideoRef.current;
      if (rv) {
        const remoteStream = rv.srcObject;
        if (remoteStream) srcs.push(ac.createMediaStreamSource(remoteStream));
      }
      srcs.forEach((s) => s.connect(dest));
      const rec = new MediaRecorder(dest.stream);
      mediaRecRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const dataUrl = String(reader.result);
            const r = await fetch("/api/v1/ai/transcribe", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ audioDataUrl: dataUrl }) });
            const j = await r.json();
            if (j?.text) {
              setTranscript((prev) => prev + (prev ? "\n" : "") + j.text);
              try { const rid = room; socketRef.current?.emit("meeting:text", { roomId: rid, text: j.text }); } catch {}
            }
          } catch {}
        };
        reader.readAsDataURL(blob);
      };
      rec.start(1000);
      setTranscribing(true);
    } catch {}
  };

  const togglePiP = async () => {
    try {
      if (!pip) {
        await remoteVideoRef.current?.requestPictureInPicture?.();
        setPip(true);
      } else if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setPip(false);
      }
    } catch {}
  };

  const stopTranscription = () => {
    if (!transcribing) return;
    const recog = recogRef.current; if (recog) try { recog.stop(); } catch {}
    const rec = mediaRecRef.current; if (rec && rec.state !== "inactive") try { rec.stop(); } catch {}
    setTranscribing(false);
  };

  const shareSummary = async () => {
    const discussion = messages.map(m => `- ${m.author}: ${m.text}`).join("\n");
    const prompt = `Summarize the meeting in clear bullet points (decisions, action items with owners, risks). Use this transcript and chat log.\n\nTranscript:\n${transcript}\n\nChat:\n${discussion}`;
    try {
      const res = await fetch("/api/v1/ai/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt, n: 1, system: "You produce concise, helpful meeting summaries." }) });
      const data = await res.json();
      const summary = (data.suggestions?.[0] || "").trim();
      if (summary && callId && socketRef.current) {
        socketRef.current.emit("call:summary", { callId, summary });
        setMessages(prev => [...prev, { id: Math.random().toString(36).slice(2), text: `Summary: ${summary}`, author: "System", ts: Date.now() }]);
      }
    } catch {}
  };

  // Screen share logic
  const originalVideoTrackRef = useRef(null);
  const originalAudioTrackRef = useRef(null);

  const startShare = async () => {
    const pc = pcRef.current; if (!pc) return;
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 24 }, audio: includeSystemAudio });
      const vTrack = display.getVideoTracks()[0];
      const aTrack = display.getAudioTracks()[0];
      const vSender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      const aSender = pc.getSenders().find((s) => s.track && s.track.kind === "audio");
      if (vSender && vTrack) {
        if (!originalVideoTrackRef.current) originalVideoTrackRef.current = vSender.track;
        await vSender.replaceTrack(vTrack);
      }
      if (aSender && aTrack) {
        if (!originalAudioTrackRef.current) originalAudioTrackRef.current = aSender.track;
        await aSender.replaceTrack(aTrack);
      }
      setPresenting(true);
      const me = displayName;
      fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/present/start`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: me }) }).catch(()=>{});
      vTrack.onended = async () => { await stopShare(); };
      setPresentOptsOpen(false);
    } catch {}
  };

  useEffect(()=>{ (async()=>{ try{ const res = await fetch("/api/v1/chat/users"); const users = await res.json(); setAllUsers(Array.isArray(users)?users:[]); } catch{} })(); },[]);

  useEffect(() => {
    let cancelled = false;
    async function loadRecentCalls() {
      try {
        const res = await fetch("/api/v1/video/calls");
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const items = Array.isArray(data)
          ? data
              .filter((entry) => (entry?.mode || "video") === "video")
              .sort((a, b) => ((b.createdAt || 0) - (a.createdAt || 0)))
              .slice(0, 8)
              .map((entry) => ({
                id: entry.id,
                title: entry.title || (entry.type === "p2p" ? "1:1 video call" : "Video room"),
                status: entry.status || "created",
                startedAt: entry.createdAt || null,
                endedAt: entry.endedAt || null,
              }))
          : [];
        setRecentCallHistory(items);
      } catch {}
    }
    loadRecentCalls();
    return () => {
      cancelled = true;
    };
  }, []);

  const stopShare = async () => {
    const pc = pcRef.current; if (!pc) return;
    try {
      const vSender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      const aSender = pc.getSenders().find((s) => s.track && s.track.kind === "audio");
      const cam = localStreamRef.current?.getVideoTracks()[0] || originalVideoTrackRef.current;
      const mic = localStreamRef.current?.getAudioTracks()[0] || originalAudioTrackRef.current;
      if (vSender && cam) await vSender.replaceTrack(cam);
      if (aSender && mic) await aSender.replaceTrack(mic);
    } catch {}
    setPresenting(false);
    const me = displayName;
    fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/present/stop`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: me }) }).catch(()=>{});
  };

  async function sendInvite(){
    const email = (inviteEmail || "").trim();
    if (!email || !callId) return;
    try {
      const me = displayName;
      await fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/invite`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ emails: [email], fromName: me }) });
      toast?.success?.("Invite sent");
      setInviteEmail("");
    } catch { toast?.error?.("Failed to send invite"); }
  }

  if (!joined) {
    return (
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
        <Card className="p-6 glass-blue text-white">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <div className="aspect-video rounded-lg overflow-hidden bg-black/40 ring-1 ring-white/10">
                <video ref={preVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
              </div>
              <div className="mt-3 text-sm">
                <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${preCamOk?"bg-green-400":"bg-red-400"}`} /> Camera {preCamOk?"ready":"blocked"}</div>
                <div className="flex items-center gap-2 mt-1"><span className={`h-2 w-2 rounded-full ${preMicOk?"bg-green-400":"bg-red-400"}`} /> Microphone {preMicOk?"ready":"blocked"}</div>
                <div className="mt-2 h-2 w-full rounded bg-white/10 overflow-hidden">
                  <div className="h-full bg-emerald-400 transition-all" style={{ width: `${Math.min(100, Math.round(micLevel*100))}%` }} />
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm grid gap-3">
                <div>
                  <div className="text-xs mb-1">Microphone</div>
                  <Select value={micId} onValueChange={setMicId}>
                    <SelectTrigger><SelectValue placeholder="Select microphone" /></SelectTrigger>
                    <SelectContent>
                      {devices.mics.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs mb-1">Camera</div>
                  <Select value={camId} onValueChange={setCamId}>
                    <SelectTrigger><SelectValue placeholder="Select camera" /></SelectTrigger>
                    <SelectContent>
                      {devices.cams.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs mb-1">Speaker</div>
                  <Select value={spkId} onValueChange={setSpkId}>
                    <SelectTrigger><SelectValue placeholder="Select speaker" /></SelectTrigger>
                    <SelectContent>
                      {devices.speakers.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-2">
                    <Button size="sm" variant="secondary" onClick={async()=>{
                      try{
                        const AC = window.AudioContext || window.webkitAudioContext; const ac = new AC();
                        const osc = ac.createOscillator(); const gain = ac.createGain();
                        osc.type = "sine"; osc.frequency.value = 440; gain.gain.value = 0.06;
                        osc.connect(gain); gain.connect(ac.destination); osc.start(); setPreSpeakerOk(true);
                        setTimeout(()=>{ try{osc.stop(); ac.close();}catch{} }, 600);
                      }catch{}
                    }}>Play test tone</Button>
                    <span className={`ml-2 text-xs ${preSpeakerOk?"text-green-300":"text-white/70"}`}>{preSpeakerOk?"Heard it":"No sound?"}</span>
                  </div>
                </div>
                {preError && <div className="text-xs text-red-300">{preError}</div>}
                <div className="pt-2 flex items-center gap-3">
                  <Button variant="destructive" onClick={() => (window.location.search = "/app")}><PhoneOff className="h-4 w-4 mr-2"/>Cancel</Button>
                  <Button onClick={()=> setJoined(true)} disabled={!preCamOk || !preMicOk} className="bg-green-600 hover:bg-green-600/90"><Phone className="h-4 w-4 mr-2"/>Join now</Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>
    );
  }

  const primaryParticipant = participants[0] ?? { name: displayName, role: "Host" };
  const secondaryParticipants = participants.slice(1);
  const meetingTitle = participants.length > 2 ? "Group Video Call" : "Video Call";
  const statusText = joined ? "Joined call via mobile scan" : "Left the call";
  const activityPerson = secondaryParticipants[0]?.name || primaryParticipant.name || "Participant";
  const activityText = banner || `${activityPerson} ${joined ? "has joined" : "has left"} the video call via QR scan on their phone.`;
  const controlButtonClass = "flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition-all hover:bg-white/20 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141b2d]";
  const controlPillClass = "flex flex-wrap items-center gap-3 rounded-full bg-[#141b2d]/95 px-4 py-2 shadow-2xl ring-1 ring-white/10 backdrop-blur";
  const getAvatarUrl = (seed) => `https://i.pravatar.cc/80?u=${encodeURIComponent(String(seed || "guest"))}`;

  const renderStage = () => {
    if (presenting) {
      return (
        <div className="relative w-full max-w-5xl aspect-video rounded-2xl border border-white/12 bg-[#101933] shadow-2xl">
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={startShare} title="Change source" className="bg-white/10 text-white hover:bg-white/20">
              Change source
            </Button>
            <Button size="sm" variant="destructive" onClick={stopShare} title="Stop sharing">
              Stop
            </Button>
          </div>
          <div className="flex h-full w-full items-center justify-center text-base text-white/80">
            You are presenting your screen
          </div>
        </div>
      );
    }

    if (pip) {
      return (
        <div className="flex w-full max-w-lg flex-col items-center justify-center rounded-2xl bg-white p-10 text-center text-slate-900 shadow-2xl">
          <div className="mb-4 h-40 w-56 rounded-lg bg-muted" />
          <h3 className="text-xl font-semibold">Your meeting is in another window</h3>
          <p className="mt-2 text-sm text-slate-600">Picture-in-Picture mode allows you to stay in the call while multitasking</p>
        </div>
      );
    }

    if (layout === "tiled") {
      return (
        <div className="w-full max-w-5xl rounded-2xl border border-white/10 bg-black/30 p-2 shadow-2xl">
          <MultiPeerCall room={room} displayName={displayName} />
        </div>
      );
    }

    if (layout === "sidebar") {
      return (
        <div className="grid w-full max-w-5xl grid-cols-12 gap-4">
          <div className="relative col-span-9 aspect-video overflow-hidden rounded-2xl border border-white/12 bg-black shadow-2xl">
            <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.video ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.video ? "Camera on" : "Camera off"}>
                {remoteStatus.video ? <Video className="h-4 w-4 text-white" /> : <VideoOff className="h-4 w-4 text-white" />}
              </span>
              <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.audio ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.audio ? "Mic on" : "Mic off"}>
                {remoteStatus.audio ? <Mic className="h-4 w-4 text-white" /> : <MicOff className="h-4 w-4 text-white" />}
              </span>
            </div>
            {pollSubtitle && (
              <div className="absolute left-1/2 bottom-4 -translate-x-1/2 rounded-md bg-black/70 px-3 py-1.5 text-xs">
                Poll: {pollSubtitle}
              </div>
            )}
          </div>
          <div className="col-span-3 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-video rounded-xl border border-white/12 bg-white/5" />
            ))}
          </div>
        </div>
      );
    }

    if (layout === "spotlight") {
      return (
        <div className="relative w-full max-w-5xl aspect-video overflow-hidden rounded-2xl border border-white/12 bg-black shadow-2xl">
          <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.video ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.video ? "Camera on" : "Camera off"}>
              {remoteStatus.video ? <Video className="h-4 w-4 text-white" /> : <VideoOff className="h-4 w-4 text-white" />}
            </span>
            <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.audio ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.audio ? "Mic on" : "Mic off"}>
              {remoteStatus.audio ? <Mic className="h-4 w-4 text-white" /> : <MicOff className="h-4 w-4 text-white" />}
            </span>
          </div>
          <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-4 py-1 text-sm font-medium">
            {primaryParticipant.name}
          </div>
          <div className="absolute bottom-4 right-4 max-w-xs rounded-md bg-black/70 px-3 py-2 text-xs">
            {activityText}
          </div>
          <div className="absolute top-4 right-4 w-36 sm:w-44 aspect-video overflow-hidden rounded-xl border border-white/12 bg-black/60 shadow-lg">
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
            <div className="absolute top-2 left-2 flex gap-1">
              <span className={`grid h-6 w-6 place-items-center rounded-full ${localStatus.video ? "bg-emerald-500/80" : "bg-red-500/80"}`}>
                {localStatus.video ? <Video className="h-3 w-3 text-white" /> : <VideoOff className="h-3 w-3 text-white" />}
              </span>
              <span className={`grid h-6 w-6 place-items-center rounded-full ${localStatus.audio ? "bg-emerald-500/80" : "bg-red-500/80"}`}>
                {localStatus.audio ? <Mic className="h-3 w-3 text-white" /> : <MicOff className="h-3 w-3 text-white" />}
              </span>
            </div>
          </div>
          {pollSubtitle && (
            <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-black/65 px-4 py-1 text-xs">
              Poll: {pollSubtitle}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative w-full max-w-5xl aspect-video overflow-hidden rounded-2xl border border-white/12 bg-black shadow-2xl">
        <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.video ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.video ? "Camera on" : "Camera off"}>
            {remoteStatus.video ? <Video className="h-4 w-4 text-white" /> : <VideoOff className="h-4 w-4 text-white" />}
          </span>
          <span className={`grid h-8 w-8 place-items-center rounded-full ${remoteStatus.audio ? "bg-emerald-500/80" : "bg-red-500/80"}`} title={remoteStatus.audio ? "Mic on" : "Mic off"}>
            {remoteStatus.audio ? <Mic className="h-4 w-4 text-white" /> : <MicOff className="h-4 w-4 text-white" />}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-4 py-1 text-sm font-medium">
          {primaryParticipant.name}
        </div>
        <div className="absolute bottom-4 right-4 max-w-xs rounded-md bg-black/70 px-3 py-2 text-xs">
          {activityText}
        </div>
        <div className="absolute top-4 right-4 w-36 sm:w-44 aspect-video overflow-hidden rounded-xl border border-white/12 bg-black/60 shadow-lg">
          <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
          <div className="absolute top-2 left-2 flex gap-1">
            <span className={`grid h-6 w-6 place-items-center rounded-full ${localStatus.video ? "bg-emerald-500/80" : "bg-red-500/80"}`}>
              {localStatus.video ? <Video className="h-3 w-3 text-white" /> : <VideoOff className="h-3 w-3 text-white" />}
            </span>
            <span className={`grid h-6 w-6 place-items-center rounded-full ${localStatus.audio ? "bg-emerald-500/80" : "bg-red-500/80"}`}>
              {localStatus.audio ? <Mic className="h-3 w-3 text-white" /> : <MicOff className="h-3 w-3 text-white" />}
            </span>
          </div>
        </div>
        {pollSubtitle && (
          <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-black/65 px-4 py-1 text-xs">
            Poll: {pollSubtitle}
          </div>
        )}
      </div>
    );
  };

  const renderThumbnails = () => {
    if (presenting || pip) return null;
    const previewParticipants = secondaryParticipants.slice(0, 3);
    return (
      <div className="flex flex-wrap justify-center gap-3 px-6 pb-4">
        <div className="flex h-28 w-36 flex-col items-center justify-center rounded-xl border border-white/15 bg-[#19223a] p-3 text-center text-sm shadow-inner">
          <div className={`mb-2 grid h-12 w-12 place-items-center rounded-full ${recordingVoice ? "bg-red-500/80" : "bg-white/10"} text-white`}>
            <Mic className="h-5 w-5" />
          </div>
          <div className="truncate font-medium">{displayName || "You"}</div>
          <div className="text-xs text-white/60">You</div>
        </div>
        {previewParticipants.map((p) => (
          <div key={p.id || p.name} className="relative h-28 w-36 overflow-hidden rounded-xl border border-white/10 bg-[#1f2a44] text-white shadow-lg">
            <img src={getAvatarUrl(p.id || p.name)} alt={p.name} className="h-full w-full object-cover" />
            <div className="absolute bottom-1 left-2 rounded bg-black/45 px-2 py-0.5 text-xs font-medium">{p.name}</div>
            <div className="absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-[10px]">🔇</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 relative">
      <div className="futurist-aurora" aria-hidden />
      <div className="grid grid-cols-12 gap-6">
        <div className={`col-span-12 ${showParticipants ? "lg:col-span-8" : "lg:col-span-12"}`}>
          <Card className="device-frame relative overflow-hidden glass-blue text-white border-0">
            {!presenting ? (
              pip ? (
                <div className="aspect-video grid place-items-center bg-white text-center">
                  <div>
                    <div className="mx-auto mb-4 h-40 w-56 rounded-lg bg-muted" />
                    <h3 className="text-xl font-semibold text-slate-900">Your meeting is in another window</h3>
                    <p className="text-sm text-slate-600">Picture-in-Picture mode allows you to stay in the call while multitasking</p>
                  </div>
                </div>
              ) : layout === "tiled" ? (
                <div className="p-2">
                  <MultiPeerCall room={room} displayName={displayName} />
                </div>
              ) : layout === "spotlight" ? (
                <div className="relative aspect-video glass-blue">
                  <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover rounded-lg" />
                  <div className="absolute left-2 top-2 flex items-center gap-1">
                    <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.video?"bg-emerald-500/80":"bg-red-500/80"}`} title={remoteStatus.video?"Camera on":"Camera off"}>{remoteStatus.video? <Video className="h-3.5 w-3.5 text-white"/> : <VideoOff className="h-3.5 w-3.5 text-white"/>}</span>
                    <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.audio?"bg-emerald-500/80":"bg-red-500/80"}`} title={remoteStatus.audio?"Mic on":"Mic off"}>{remoteStatus.audio? <Mic className="h-3.5 w-3.5 text-white"/> : <MicOff className="h-3.5 w-3.5 text-white"/>}</span>
                  </div>
                  <div className="absolute bottom-4 right-4 w-56 aspect-video rounded-md overflow-hidden shadow-lg">
                    <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
                    <div className="absolute left-2 top-2 flex items-center gap-1">
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${localStatus.video?"bg-emerald-500/80":"bg-red-500/80"}`} title={localStatus.video?"Camera on":"Camera off"}>{localStatus.video? <Video className="h-3.5 w-3.5 text-white"/> : <VideoOff className="h-3.5 w-3.5 text-white"/>}</span>
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${localStatus.audio?"bg-emerald-500/80":"bg-red-500/80"}`} title={localStatus.audio?"Mic on":"Mic off"}>{localStatus.audio? <Mic className="h-3.5 w-3.5 text-white"/> : <MicOff className="h-3.5 w-3.5 text-white"/>}</span>
                    </div>
                  </div>
                  {pollSubtitle && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-3 max-w-[80%] rounded bg-black/70 text-white text-xs px-3 py-1.5">
                      Poll: {pollSubtitle}
                    </div>
                  )}
                </div>
              ) : layout === "sidebar" ? (
                <div className="grid grid-cols-12 gap-4 p-4">
                  <div className="col-span-9 aspect-video rounded-lg glass-blue overflow-hidden relative">
                    <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
                    <div className="absolute left-2 top-2 flex items-center gap-1">
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.video?"bg-emerald-500/80":"bg-red-500/80"}`}>{remoteStatus.video? <Video className="h-3.5 w-3.5 text-white"/> : <VideoOff className="h-3.5 w-3.5 text-white"/>}</span>
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.audio?"bg-emerald-500/80":"bg-red-500/80"}`}>{remoteStatus.audio? <Mic className="h-3.5 w-3.5 text-white"/> : <MicOff className="h-3.5 w-3.5 text-white"/>}</span>
                    </div>
                    {pollSubtitle && (
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-3 max-w-[90%] rounded bg-black/70 text-white text-xs px-3 py-1.5">
                        Poll: {pollSubtitle}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="aspect-video rounded-md glass-blue" />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                  <div className="relative aspect-video rounded-lg glass-blue">
                    <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover rounded-lg" />
                    <div className="absolute left-2 top-2 flex items-center gap-1">
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${localStatus.video?"bg-emerald-500/80":"bg-red-500/80"}`}>{localStatus.video? <Video className="h-3.5 w-3.5 text-white"/> : <VideoOff className="h-3.5 w-3.5 text-white"/>}</span>
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${localStatus.audio?"bg-emerald-500/80":"bg-red-500/80"}`}>{localStatus.audio? <Mic className="h-3.5 w-3.5 text-white"/> : <MicOff className="h-3.5 w-3.5 text-white"/>}</span>
                    </div>
                  </div>
                  <div className="relative aspect-video rounded-lg glass-blue">
                    <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover rounded-lg" />
                    <div className="absolute left-2 top-2 flex items-center gap-1">
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.video?"bg-emerald-500/80":"bg-red-500/80"}`}>{remoteStatus.video? <Video className="h-3.5 w-3.5 text-white"/> : <VideoOff className="h-3.5 w-3.5 text-white"/>}</span>
                      <span className={`h-6 w-6 grid place-items-center rounded-full ${remoteStatus.audio?"bg-emerald-500/80":"bg-red-500/80"}`}>{remoteStatus.audio? <Mic className="h-3.5 w-3.5 text-white"/> : <MicOff className="h-3.5 w-3.5 text-white"/>}</span>
                    </div>
                    {pollSubtitle && (
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-3 max-w-[90%] rounded bg-black/70 text-white text-xs px-3 py-1.5">
                        Poll: {pollSubtitle}
                      </div>
                    )}
                  </div>
                  <div className="relative aspect-video rounded-lg bg-muted grid place-items-center text-sm text-muted-foreground">Kathryn Murphy</div>
                </div>
              )
            ) : (
              <div className="relative aspect-video glass-blue">
                <div className="absolute top-3 right-3 flex gap-2 z-10">
                  <Button size="sm" variant="secondary" onClick={startShare} title="Change source">Change source</Button>
                  <Button size="sm" variant="destructive" onClick={stopShare} title="Stop sharing">Stop</Button>
                </div>
                <div className="h-full w-full grid place-items-center">
                  <span className="text-white/80">You are presenting your screen</span>
                </div>
              </div>
            )}

            {banner && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-white text-slate-900 text-sm px-3 py-1.5 rounded shadow">
                {banner}
              </div>
            )}

            <div className="absolute left-0 right-0 bottom-0 px-4 py-4 flex items-center justify-center gap-3 bg-gradient-to-t from-[#0f1320]/95 to-transparent zoom-bar">
              <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" onClick={()=>{
                setCamera((v)=>!v);
                const stream = localStreamRef.current; if (!stream) return;
                stream.getVideoTracks().forEach((t) => (t.enabled = !camera));
              }} title="Toggle camera">
                {camera ? <Video className="h-4 w-4"/> : <VideoOff className="h-4 w-4"/>}
              </Button>
              <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" onClick={()=>{
                setMuted((v)=>!v);
                const stream = localStreamRef.current; if (!stream) return;
                stream.getAudioTracks().forEach((t) => (t.enabled = muted));
              }} title="Toggle mic">
                {muted ? <MicOff className="h-4 w-4"/> : <Mic className="h-4 w-4"/>}
              </Button>

              {!presenting ? (
                <>
                  <Dialog open={presentOptsOpen} onOpenChange={setPresentOptsOpen}>
                    <DialogTrigger asChild>
                      <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="Share screen"><MonitorUp className="h-4 w-4"/></Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader><DialogTitle>Present your screen</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={includeSystemAudio} onChange={(e)=>setIncludeSystemAudio(e.target.checked)} />
                          Share system audio (when supported)
                        </label>
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="secondary" onClick={()=>setPresentOptsOpen(false)}>Cancel</Button>
                          <Button onClick={startShare}>Start sharing</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <>
                  <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" onClick={stopShare} title="Stop sharing"><MonitorX className="h-4 w-4"/></Button>
                </>
              )}

              <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" onClick={togglePiP} title={pip?"Exit PiP":"Open pic-in-pic"}><MonitorPause className="h-4 w-4"/></Button>
              <Button variant="secondary" className={`ctrl rounded-full h-11 w-11 p-0 ${transcribing ? "bg-white/20" : ""}`} onClick={()=>{ transcribing ? stopTranscription() : startTranscription(); }} title={transcribing?"Stop captions":"Start captions"}>CC</Button>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="Add people"><Plus className="h-4 w-4"/></Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader><DialogTitle>Add People</DialogTitle></DialogHeader>
                  <div className="mt-2">
                    <div className="flex gap-2">
                      <Input placeholder="email@example.com" value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); sendInvite(); } }} />
                      <Button onClick={sendInvite}>Invite</Button>
                    </div>
                    <div className="mt-3 grid gap-2">
                      {participants.map(p=> (
                         <div key={p.id} className="flex items-center justify-between rounded-md border p-2">
                           <div className="flex items-center gap-3">
                             <Avatar className="h-8 w-8">
                  <AvatarImage src={`https://i.pravatar.cc/40?u=${encodeURIComponent(p.name || p.id)}`} alt={p.name} referrerPolicy="no-referrer" />
                  <AvatarFallback>{((p.name || "").split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                </Avatar>
                             <div>
                               <div className="font-medium leading-none">{p.name}</div>
                               <div className="text-xs text-muted-foreground">{p.role}</div>
                             </div>
                           </div>
                           <Button size="sm" variant="secondary" disabled>Added</Button>
                         </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <div className="text-sm font-medium mb-2">All users</div>
                      <ul className="grid gap-2 max-h-[260px] overflow-auto">
                        {allUsers.map(u=> (
                          <li key={u.id} className="flex items-center justify-between rounded-md border p-2">
                            <div className="flex items-center gap-3">
                              <img src={`https://i.pravatar.cc/40?u=${encodeURIComponent(u.name || u.email || u.id)}`} alt={u.name || u.email} className="h-8 w-8 rounded-full object-cover" />
                              <div>
                                <div className="text-sm font-medium leading-none">{u.name || u.email}</div>
                                <div className="text-xs text-muted-foreground">{u.email}</div>
                              </div>
                            </div>
                            <Button size="sm" onClick={async()=>{ setInviteEmail(u.email||""); await sendInvite(); }}>Add</Button>
                          </li>
                        ))}
                        {allUsers.length===0 && <li className="text-xs text-muted-foreground">No users</li>}
                      </ul>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="More options"><MoreVertical className="h-4 w-4"/></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => setOpenLayout(true)}>Video Layout</DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => { try { await remoteVideoRef.current?.requestPictureInPicture?.(); setPip(true); } catch {} }}>Open pic-in-pic mode</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { transcribing ? stopTranscription() : startTranscription(); }}>{transcribing ? "Stop captions (STT)" : "Start captions (STT)"}</DropdownMenuItem>
                  <DropdownMenuItem onClick={shareSummary}>Share AI summary</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOpenReport(true)}>Report a problem</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOpenAbuse(true)}>Report abuse</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOpenHelp(true)}>Help</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setOpenSettings(true)}>Settings</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="Toggle side panel" onClick={()=> { if (window.innerWidth < 1024) setOpenParticipantsSheet(true); else setShowParticipants(v=>!v); }}><Users className="h-4 w-4"/></Button>

              <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="Open chat" onClick={()=> setOpenChatSheet(true)}><MessageSquare className="h-4 w-4"/></Button>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="ctrl rounded-full h-11 w-11 p-0" title="AI Assistant"><Sparkles className="h-4 w-4"/></Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl">
                  <DialogHeader><DialogTitle>AI Assistant</DialogTitle></DialogHeader>
                  <AgentPanel context="call" messages={messages.map(m=>({author:m.author,text:m.text,ts:m.ts}))} />
                </DialogContent>
              </Dialog>

              <Button variant="destructive" className="zoom-end rounded-full h-11 px-6" onClick={async ()=>{ if (callId) { try { await fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/end`, { method: "POST" }); } catch {} } window.location.search = "/app"; }}><PhoneOff className="h-4 w-4 mr-2"/>End</Button>

              <Dialog open={openLayout} onOpenChange={setOpenLayout}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Video Layout</DialogTitle></DialogHeader>
                  <RadioGroup value={layout} onValueChange={(v)=>setLayout(v)} className="grid gap-3">
                    {["default","tiled","spotlight","sidebar"].map((l)=> (
                      <label key={l} className="flex items-center gap-3 rounded-md border p-2 cursor-pointer">
                        <RadioGroupItem value={l} />
                        <div className="flex-1 capitalize">{l}</div>
                        <div className="h-10 w-16 rounded bg-muted" />
                      </label>
                    ))}
                  </RadioGroup>
                </DialogContent>
              </Dialog>

              <Sheet open={openReport} onOpenChange={setOpenReport}>
                <SheetContent side="right" className="sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle>Report an issue</SheetTitle>
                  </SheetHeader>
                  <div className="mt-4 space-y-3">
                    <Select value={issueType} onValueChange={setIssueType}>
                      <SelectTrigger><SelectValue placeholder="Select an option" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="audio">Audio issue</SelectItem>
                        <SelectItem value="video">Video issue</SelectItem>
                        <SelectItem value="screen">Screen sharing issue</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Describe your issue" />
                    <div>
                      <Button variant="secondary" onClick={takeScreenshot}>Take a screenshot</Button>
                      {shot && <img src={shot} alt="screenshot" className="mt-2 rounded border" />}
                    </div>
                    <div className="flex items-center justify-end"><Button>Send</Button></div>
                  </div>
                </SheetContent>
              </Sheet>

              <Dialog open={openAbuse} onOpenChange={setOpenAbuse}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Report abuse</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Select value={abuseType} onValueChange={setAbuseType}>
                      <SelectTrigger><SelectValue placeholder="Select abuse type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spam">Spam or unwanted content</SelectItem>
                        <SelectItem value="fraud">Fraud, phishing and other practices</SelectItem>
                        <SelectItem value="harassment">Harassment and hateful content</SelectItem>
                        <SelectItem value="sexual">Unwanted sexual content</SelectItem>
                        <SelectItem value="violence">Violence and gore</SelectItem>
                        <SelectItem value="child">Child endangerment</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <div>
                      <div className="text-sm mb-1">Full name</div>
                      <Select value={abuseUserId} onValueChange={setAbuseUserId}>
                        <SelectTrigger><SelectValue placeholder="Select participant" /></SelectTrigger>
                        <SelectContent>
                          {participants.map((p)=> (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input placeholder="Provide information on the issue" value={abuseNotes} onChange={(e)=>setAbuseNotes(e.target.value)} />
                    <div className="flex items-center justify-end"><Button onClick={async ()=>{
                      const p = participants.find(x=>x.id===abuseUserId);
                      const text = `Abuse report (${abuseType || "unspecified"}) against ${p?.name || "unknown"}. Notes: ${abuseNotes}`;
                      try { await fetch(`/api/v1/video/calls/${encodeURIComponent(callId)}/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ authorEmail: "you@example.com", text }) }); } catch {}
                      setOpenAbuse(false);
                    }}>Report</Button></div>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={openHelp} onOpenChange={setOpenHelp}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Help</DialogTitle></DialogHeader>
                  <div className="space-y-2 text-sm">
                    <Input placeholder="Search Help Centre" />
                    <ul className="list-disc pl-6 space-y-1">
                      <li>How to use</li>
                      <li>Screen sharing during a video call</li>
                      <li>Record a video call</li>
                      <li>Live stream a video call</li>
                      <li>Virtual Background</li>
                      <li>Video and audio quality</li>
                    </ul>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={openSettings} onOpenChange={setOpenSettings}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader><DialogTitle>Settings</DialogTitle></DialogHeader>
                  <Tabs defaultValue="audio" className="w-full">
                    <TabsList className="grid grid-cols-5 mb-4">
                      <TabsTrigger value="audio">Audio</TabsTrigger>
                      <TabsTrigger value="video">Video</TabsTrigger>
                      <TabsTrigger value="general">General</TabsTrigger>
                      <TabsTrigger value="captions">Captions</TabsTrigger>
                      <TabsTrigger value="message">Message</TabsTrigger>
                    </TabsList>
                    <TabsContent value="audio" className="space-y-3">
                      <div>
                        <div className="text-sm mb-1">Microphone</div>
                        <Select value={micId} onValueChange={setMicId}>
                          <SelectTrigger><SelectValue placeholder="Select microphone" /></SelectTrigger>
                          <SelectContent>
                            {devices.mics.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-sm mb-1">Speaker</div>
                        <Select value={spkId} onValueChange={setSpkId}>
                          <SelectTrigger><SelectValue placeholder="Select speaker" /></SelectTrigger>
                          <SelectContent>
                            {devices.speakers.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2 text-sm">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={audioNoiseSuppression} onChange={(e)=>setAudioNoiseSuppression(e.target.checked)} /> Noise suppression</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={audioEchoCancellation} onChange={(e)=>setAudioEchoCancellation(e.target.checked)} /> Echo cancellation</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={audioAutoGain} onChange={(e)=>setAudioAutoGain(e.target.checked)} /> Auto gain control</label>
                        <div className="flex items-center justify-end"><Button size="sm" onClick={applyAudioSettings}>Apply</Button></div>
                      </div>
                    </TabsContent>
                    <TabsContent value="video" className="space-y-3">
                      <div>
                        <div className="text-sm mb-1">Camera</div>
                        <Select value={camId} onValueChange={setCamId}>
                          <SelectTrigger><SelectValue placeholder="Select camera" /></SelectTrigger>
                          <SelectContent>
                            {devices.cams.map((d)=> <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-sm mb-1">Resolution</div>
                        <Select value={videoResolution} onValueChange={setVideoResolution}>
                          <SelectTrigger><SelectValue placeholder="Select resolution" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="480p">480p</SelectItem>
                            <SelectItem value="720p">720p</SelectItem>
                            <SelectItem value="1080p">1080p</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center justify-end mt-2"><Button size="sm" onClick={applyVideoResolution}>Apply</Button></div>
                      </div>
                    </TabsContent>
                    <TabsContent value="general">No general settings</TabsContent>
                    <TabsContent value="captions">Captions coming soon</TabsContent>
                    <TabsContent value="message">Messaging options</TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <HighlightsPanel roomId={room} socket={socketRef.current} />
            <PollsPanel roomId={room} socket={socketRef.current} />
          </div>
        </div>

        <div className={`${showParticipants ? "hidden lg:block" : "hidden"} lg:col-span-4`}>
          <div className="grid gap-3">
            <Card className="glass-card p-3">
              <div className="glass-header-blue flex items-center justify-between"><b>Participants</b><span className="text-xs">{participants.length}</span></div>
              <ul className={participants.length <= 2 ? "mt-3 grid gap-3" : "mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"}>
                {participants.map((p)=> (
                  <li key={p.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 dark:bg-slate-900/30 p-2 hover:bg-white/10 transition">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="h-9 w-9 ring-2 ring-indigo-400">
                          <AvatarImage src={`https://i.pravatar.cc/40?u=${encodeURIComponent(p.name || p.id)}`} alt={p.name} referrerPolicy="no-referrer" />
                          <AvatarFallback>{((p.name || "").split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                        </Avatar>
                        <span className="absolute -top-0.5 -left-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-slate-900" />
                        <div className="mt-1 flex justify-center gap-1">
                          <Button size="icon" variant="secondary" className="h-6 w-6 bg-white/10"><Mic className="h-3 w-3"/></Button>
                          <Button size="icon" variant="secondary" className="h-6 w-6 bg-white/10"><Video className="h-3 w-3"/></Button>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium leading-none">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.role}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="glass-card p-3">
              <div className="glass-header-blue flex items-center justify-between"><b>In-call messages</b><span className="text-xs">{messages.length}</span></div>
              <ul ref={inCallListRef} className="mt-2 space-y-2 max-h-[240px] overflow-auto pr-2">
                {messages.map((m)=> {
                  const isMe = m.author === "You";
                  const name = isMe ? "You" : (m.author || "Member");
                  const avatarSeed = m.authorEmail || m.authorName || name;
                  const avatarUrl = `https://i.pravatar.cc/40?u=${encodeURIComponent(String(avatarSeed))}`;
                  return (
                    <li key={m.id} className={`flex items-end gap-2 ${isMe ? "justify-end" : "justify-start"}`}>
                      {!isMe && (
                        <Avatar className="h-6 w-6 ring-2 ring-indigo-400">
                          <AvatarImage src={avatarUrl} alt={name} referrerPolicy="no-referrer" />
                          <AvatarFallback>{(name.split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                        </Avatar>
                      )}
                      <div className={`max-w-[80%] px-3 py-2 text-sm ${isMe ? "bubble-out" : "bubble-in"}`}>
                        <div className="text-[11px] opacity-80 mb-0.5">{name}</div>
                        <div>{m.text}</div>
                      </div>
                      {isMe && (
                        <Avatar className="h-6 w-6 ring-2 ring-sky-400">
                          <AvatarImage src={avatarUrl} alt={name} referrerPolicy="no-referrer" />
                          <AvatarFallback>{(name.split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                        </Avatar>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-2 composer px-2 py-1 text-foreground">
                <input ref={attachInputRef} type="file" className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if(f){ send(`[attachment] ${f.name}`); e.target.value=""; } }} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-foreground" title="Attach" onClick={()=> attachInputRef.current?.click()}><Paperclip className="h-4 w-4"/></Button>
                <Input type="text" value={chat} onChange={(e)=>setChat(e.target.value)} placeholder="Type a message..." onKeyDown={(e)=>{ if (e.key === "Enter") send(); }} className="border-0 shadow-none focus-visible:ring-0 bg-white text-slate-900 placeholder:text-slate-500" />
                <EmojiPicker onSelect={(e)=> setChat((v)=> v + e)}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-foreground" title="Emoji"><Smile className="h-4 w-4"/></Button>
                </EmojiPicker>
                <Button onClick={()=>send()} size="sm" className="rounded-full px-3"><Send className="h-4 w-4"/></Button>
              </div>
            </Card>

            <Card className="glass-card p-3">
              <div className="flex items-center justify-between"><b>Captions</b>{transcribing ? <span className="text-xs text-green-500">Live</span> : <span className="text-xs text-muted-foreground">Off</span>}</div>
              <pre className="mt-2 whitespace-pre-wrap text-xs rounded-md border p-2 min-h-[150px] max-h-[200px] overflow-auto">{transcript || ""}</pre>
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={()=> transcribing ? stopTranscription() : startTranscription()}>{transcribing ? "Stop" : "Start"}</Button>
                <Button size="sm" variant="secondary" onClick={()=> setTranscript("")}>Clear</Button>
                <Button size="sm" onClick={shareSummary}>Share AI summary</Button>
              </div>
            </Card>

            <Card className="glass-card p-3">
              <div className="glass-header-blue flex items-center justify-between"><b>Recent video calls</b><span className="text-xs">{recentCallHistory.length}</span></div>
              {recentCallHistory.length ? (
                <ul className="mt-2 space-y-2 text-sm">
                  {recentCallHistory.map((entry) => {
                    const startedLabel = entry.startedAt ? new Date(entry.startedAt).toLocaleString() : "Unknown start";
                    const statusLabel = entry.status === "ended" && entry.endedAt
                      ? `Ended ${new Date(entry.endedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                      : entry.status === "ended"
                        ? "Ended"
                        : "Active";
                    return (
                      <li key={entry.id} className="flex items-start justify-between gap-3 rounded-lg bg-white/5 p-2 text-left dark:bg-slate-900/40">
                        <span>
                          <span className="block font-semibold leading-tight">{entry.title}</span>
                          <span className="text-xs text-muted-foreground">{startedLabel}</span>
                        </span>
                        <span className="text-[11px] text-muted-foreground">{statusLabel}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="mt-2 text-xs text-muted-foreground">No call history yet.</div>
              )}
            </Card>

            <SocketDiagnostics className="glass-card p-3 bg-white/5 border-white/10 text-white" />

          </div>
        </div>
      </div>

      <Sheet open={openParticipantsSheet} onOpenChange={setOpenParticipantsSheet}>
        <SheetContent side="left" className="w-[360px] sm:w-[420px]">
          <SheetHeader>
            <SheetTitle>Participants</SheetTitle>
          </SheetHeader>
          <div className="mt-3 grid gap-2">
            {participants.map((p)=> (
              <div key={p.id} className="flex items-center justify-between rounded-md border p-2">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={`https://i.pravatar.cc/40?u=${encodeURIComponent(p.name || p.id)}`} alt={p.name} referrerPolicy="no-referrer" />
                      <AvatarFallback>{((p.name || "").split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                    </Avatar>
                    <div className="mt-1 flex justify-center gap-1">
                      <Button size="icon" variant="secondary" className="h-6 w-6 bg-white/10"><Mic className="h-3 w-3"/></Button>
                      <Button size="icon" variant="secondary" className="h-6 w-6 bg-white/10"><Video className="h-3 w-3"/></Button>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium leading-none">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={openChatSheet} onOpenChange={setOpenChatSheet}>
        <SheetContent side="right" className="w-[360px] sm:w-[420px] glass-blue text-white z-[60]">
          <SheetHeader>
            <SheetTitle>Chat</SheetTitle>
          </SheetHeader>
          <div className="mt-2 space-y-2">
            <ul ref={chatListRef} className="space-y-2 max-h-[60vh] overflow-auto pr-2">
              {messages.map((m)=>{
                const isMe = m.author === "You";
                const name = isMe ? "You" : (m.author || "Member");
                const avatarSeed = m.authorEmail || m.authorName || name;
                const avatarUrl = `https://i.pravatar.cc/40?u=${encodeURIComponent(String(avatarSeed))}`;
                return (
                  <li key={m.id} className={`flex items-end gap-2 ${isMe ? "justify-end" : "justify-start"}`}>
                    {!isMe && (
                      <Avatar className="h-7 w-7 ring-2 ring-indigo-400">
                        <AvatarImage src={avatarUrl} alt={name} referrerPolicy="no-referrer" />
                        <AvatarFallback>{(name.split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`max-w-[78%] px-3 py-2 text-sm ${isMe ? "bubble-out" : "bubble-in"}`}>
                      <div className="text-[11px] opacity-80 mb-0.5">{name}</div>
                      <div>{m.text}</div>
                    </div>
                    {isMe && (
                      <Avatar className="h-7 w-7 ring-2 ring-sky-400">
                        <AvatarImage src={avatarUrl} alt={name} referrerPolicy="no-referrer" />
                        <AvatarFallback>{(name.split(" ").map(s=>s[0]).join("").slice(0,2)) || "U"}</AvatarFallback>
                      </Avatar>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="composer px-2 py-1 text-white">
              <input ref={attachSheetRef} type="file" className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if(f){ send(`[attachment] ${f.name}`); e.target.value=""; } }} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-white" title="Attach" onClick={()=> attachSheetRef.current?.click()}><Paperclip className="h-4 w-4"/></Button>
              <Input type="text" value={chat} onChange={(e)=>setChat(e.target.value)} placeholder="Type a message..." onKeyDown={(e)=>{ if (e.key === "Enter") send(); }} className="border-0 shadow-none focus-visible:ring-0 bg-white text-slate-900 placeholder:text-slate-500" />
              <EmojiPicker onSelect={(e)=> setChat((v)=> v + e)}>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white" title="Emoji"><Smile className="h-4 w-4"/></Button>
              </EmojiPicker>
              <Button onClick={()=>send()} size="sm" className="rounded-full px-3"><Send className="h-4 w-4"/></Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
